import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useCreateProfile } from "../../../api/profile";
import { useSnackbar } from "../../../shared/components/snackbar/Snackbar";
import type { ProblemDetails } from "../../../shared/types/problem-details.type";
import { getProblemDetailsErrorMessage } from "../../../utils/problem-details-utilities/problem-details-utils";
import { profileExportErrorTypes } from "../profile.constant";
import { sanitiseCreateProfilePayload } from "../profile-utils/sanitiseCreateProfilePayload";
import type {
  BusinessCreateProfilePayload,
  CreateProfilePayload,
  CreateProfileResponse,
  IndividualCreateProfilePayload,
  SoleTraderCreateProfilePayload,
} from "../types/createProfile.type";
import { CreateProfileType } from "../types/createProfile.type";
import {
  buildCreateProfileModalPayload,
  type CreateProfileModalFormValues,
} from "./buildCreateProfileModalPayload";

interface CreateProfileResult {
  profileId: string;
  profileType: CreateProfileType;
}

interface UseCreateProfileModalOptions {
  onNameConflict?: (_message: string) => void;
}

const getTranslationString = (
  translate: TFunction,
  key: string,
  options?: Record<string, unknown>
): string => {
  const result =
    options === undefined ? translate(key) : translate(key, options);
  return typeof result === "string" ? result : key;
};

export const getCreateProfileErrorMessage = (
  error: ProblemDetails,
  translate: TFunction
): string =>
  getProblemDetailsErrorMessage(
    error,
    getTranslationString(
      translate,
      "common:apiResponse.unexpectedErrorOccurred"
    )
  );

export const useCreateProfileModal = (
  options?: UseCreateProfileModalOptions
): {
  createProfile: (
    _values: CreateProfileModalFormValues
  ) => Promise<CreateProfileResult | undefined>;
  isLoading: boolean;
} => {
  const { t: translate } = useTranslation(["profile", "common"]);
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const individualCreateProfile =
    useCreateProfile<IndividualCreateProfilePayload>();
  const soleTraderCreateProfile =
    useCreateProfile<SoleTraderCreateProfilePayload>();
  const businessCreateProfile =
    useCreateProfile<BusinessCreateProfilePayload>();

  const createByType = async (
    payload: CreateProfilePayload,
    profileType: CreateProfileType
  ): Promise<CreateProfileResponse> => {
    if (profileType === CreateProfileType.individual) {
      return individualCreateProfile.mutateAsync(
        payload as IndividualCreateProfilePayload
      );
    }
    if (profileType === CreateProfileType.soleTrader) {
      return soleTraderCreateProfile.mutateAsync(
        payload as SoleTraderCreateProfilePayload
      );
    }
    return businessCreateProfile.mutateAsync(
      payload as BusinessCreateProfilePayload
    );
  };

  const createProfile = async (
    values: CreateProfileModalFormValues
  ): Promise<CreateProfileResult | undefined> => {
    setIsLoading(true);
    try {
      const payload = buildCreateProfileModalPayload(values);
      const sanitizedPayload = sanitiseCreateProfilePayload(
        payload
      ) as CreateProfilePayload;
      const response = await createByType(
        sanitizedPayload,
        values.profileType
      );

      const profileId = response?.id;
      if (!profileId) {
        showSnackbar(
          getTranslationString(
            translate,
            "common:apiResponse.unexpectedErrorOccurred"
          ),
          true
        );
        return undefined;
      }

      return {
        profileId,
        profileType: values.profileType,
      };
    } catch (error) {
      const problemDetails = error as ProblemDetails;

      if (
        problemDetails?.details?.type ===
        profileExportErrorTypes.profileConflict
      ) {
        const errorMsg = getTranslationString(
          translate,
          "profile:profile.validationMsg.profileNameAlreadyExist",
          { payload: { name: values.profileName } }
        );
        options?.onNameConflict?.(errorMsg);
        return undefined;
      }

      showSnackbar(getCreateProfileErrorMessage(problemDetails, translate), true);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createProfile,
    isLoading,
  };
};
